import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, TrendingUp, User, Globe } from "lucide-react";

export default function AdminChatStats() {
  const nav = useNavigate();
  const { user } = useAdminAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) nav("/admin/login");
    else if (user && user.role !== "admin") {
      toast.error("Area riservata all'amministratore");
      nav("/");
    }
  }, [user, nav]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/chat-stats", { params: { days, limit: 20 } });
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, days]);

  if (user === undefined) return <div className="max-w-5xl mx-auto p-10 text-ink-muted">Caricamento...</div>;
  if (!user) return null;

  const maxCount = data && data.top.length > 0 ? data.top[0].count : 1;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-chat-stats">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand">
        <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
      </Link>
      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-brand/10 text-brand flex items-center justify-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">Chat analytics</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl text-ink">Domande popolari</h1>
          </div>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="chat-stats-period"
          className="h-11 px-3 bg-white border border-line rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand/20"
        >
          <option value={7}>Ultimi 7 giorni</option>
          <option value={30}>Ultimi 30 giorni</option>
          <option value={90}>Ultimi 90 giorni</option>
          <option value={365}>Ultimo anno</option>
        </select>
      </div>

      {data && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Domande totali" value={data.total_questions.toLocaleString("it-IT")} />
          <StatCard label="Domande uniche" value={data.unique_questions.toLocaleString("it-IT")} />
          <StatCard label="Periodo" value={`${data.period_days}g`} />
        </div>
      )}

      <div className="mt-8 bg-white border border-line rounded-lg shadow-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">Caricamento...</div>
        ) : !data || data.top.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">Nessuna domanda registrata nel periodo.</div>
        ) : (
          <div className="divide-y divide-line">
            {data.top.map((row, i) => {
              const pct = Math.round((row.count / maxCount) * 100);
              return (
                <div key={i} className="p-5 hover:bg-line/10" data-testid={`chat-stat-row-${i}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-brand text-xl w-8 shrink-0">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-ink leading-relaxed">{row.question}</p>
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-muted">
                        <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" />{row.count} volte</span>
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{row.reg_count} registrati</span>
                        <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{row.anon_count} anonimi</span>
                        <span className="text-ink-muted/70">ultimo: {new Date(row.last_at).toLocaleDateString("it-IT")}</span>
                      </div>
                    </div>
                    <div className="w-20 sm:w-32 shrink-0">
                      <div className="h-2 bg-line rounded-full overflow-hidden">
                        <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="mt-1 text-[10px] text-ink-muted text-right font-mono">{row.count}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 text-xs text-ink-muted">
        Suggerimento: le domande in cima sono candidati ideali per una <strong>sezione FAQ</strong> sul sito o per articoli di blog che rispondano in modo esaustivo.
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-line rounded-lg p-5 shadow-card">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-2 font-display text-3xl text-ink">{value}</div>
    </div>
  );
}
